import { useMemo, useState, type ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  Exercise,
  Plan,
  PlanSeed,
  ProgramModel,
  Session,
  UnitsPreference,
  Workout,
} from "@heraion/shared";

import { SaveSessionAsModelDialog } from "../Library/SaveAsModelDialog";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { MonthCalendar } from "../primitives/MonthCalendar";
import { SelectToggle } from "../primitives/SelectToggle";
import type { ConfirmFn, DurableWriteFn } from "../seams";
import type { UiTheme } from "../theme";
import { parseRhythmKind } from "../Progress/progressMath";
import { useRhythmKindLabels } from "../Progress/useRhythmKindLabels";
import { useAccountPref } from "../useAccountPref";
import { DiaryKindFilter, filterByKind } from "./DiaryKindFilter";
import { DiaryModeSwitch } from "./DiaryModeSwitch";
import { PlannedDayList } from "./PlannedDayList";
import { DiarySelectionBar } from "./DiarySelectionBar";
import { SelectionOrderList } from "./SelectionOrderList";
import { useDiaryCalendar } from "./useDiaryCalendar";
import { useDiarySelection } from "./useDiarySelection";

/** The model name a TextField takes (ModelSaveDialog). */
const MODEL_NAME_MAX = 80;

export type DiaryMode = "list" | "calendar";

export type WorkoutsDiaryOptions = {
  theme: UiTheme;
  /** The loaded sessions: the picked ones' names and dates come from here. */
  items: Session[] | null;
  /** #374: no older page of sessions is left to load. */
  allLoaded: boolean;
  /** #499: the reader's sub; null while it is not known. A plan they wrote
   *  for other athletes is not theirs to miss on the calendar. */
  sub: string | null;
  /** #321: the storage key of the Strength | Cardio | All choice, the account's
   *  (RHYTHM_KIND_KEY, as Progress has it); null: nobody is signed in. */
  kindKey: string | null;
  /** The wide web: a Select button beside List | Calendar. */
  showSelectButton: boolean;
  /** The phone: the bar sits at the bottom, in place of the tab bar. */
  dockedBar: boolean;
  apiFetch: ProgramApiFetch;
  durableWrite: DurableWriteFn;
  confirm: ConfirmFn;
  /** One session with its sets, read the host's way. */
  readWorkout: (id: string) => Promise<Workout>;
  /** Resolves, with the ids that are gone, once the list no longer has them. */
  deleteSessions: (ids: readonly string[]) => Promise<readonly string[]>;
  /** Opens the plan editor on a new plan made from one session. */
  onPlanSeed: (seed: PlanSeed) => void;
  /** Opens the program wizard on the model «Create program» saved. */
  onPlanFromModel: (model: Omit<ProgramModel, "id">) => void;
  onOpenPlan: (plan: Plan) => void;
  /** #374: a workout done on the calendar's day, on its summary. */
  onOpenSession: (session: Session) => void;
  onStartPlan?: (plan: Plan) => void;
  startingPlanId?: string | null;
  /** The unit the loads are read in, for the plan and the model. null while
   *  the profile is not read: Plan and Create program wait for it. */
  unit: UnitsPreference | null;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  /** A read that failed, said in words. */
  onError: (message: string) => void;
};

// #368: the diary's two modes and its selection, for both hosts. The host
// places the pieces (the phone stacks them, the wide web splits list and
// detail panes) and keeps its own reads, delete and navigation. The selection
// is useDiarySelection's, the calendar's days useDiaryCalendar's.
export function useWorkoutsDiary(o: WorkoutsDiaryOptions) {
  const { t } = useTranslation();
  const { theme } = o;
  const [mode, setMode] = useState<DiaryMode>("list");
  const sel = useDiarySelection(o);
  const { selecting, picked, end, nameOf, model } = sel;
  const cal = useDiaryCalendar({ ...o, enabled: mode === "calendar" });
  // Unset, the list shows every session: the walks stay visible (#321).
  const [kind, setKind] = useAccountPref(o.kindKey, parseRhythmKind, "all");
  const labels = useRhythmKindLabels();
  const shown = useMemo(
    () => filterByKind(o.items, kind, sel.ids),
    [o.items, kind, sel.ids],
  );

  const styles = makeStyles(theme);
  const header: ReactNode = (
    <View style={styles.headerBox}>
      <View style={styles.header}>
        <DiaryModeSwitch theme={theme} mode={mode} disabled={selecting} onChange={setMode} />
        {o.showSelectButton && mode === "list" ? (
          <SelectToggle
            theme={theme}
            active={selecting}
            onPress={() => (selecting ? end() : sel.setSelecting(true))}
            a11yIdle={{ key: "mobile.workouts.selectA11y", defaultValue: "Select workouts" }}
            a11yDone={{ key: "mobile.workouts.selectDoneA11y", defaultValue: "Done selecting workouts" }}
          />
        ) : null}
      </View>
      {mode === "list" ? (
        <View style={styles.kind}>
          <DiaryKindFilter theme={theme} kind={kind} count={shown?.length ?? null} onKind={setKind} />
        </View>
      ) : null}
    </View>
  );

  const bar: ReactNode = selecting ? (
    <DiarySelectionBar
      theme={theme}
      count={picked.length}
      busy={sel.busy}
      ready={o.unit !== null}
      deleting={sel.deleting}
      docked={o.dockedBar}
      showDone={!o.showSelectButton}
      onDone={end}
      onDelete={() => void sel.onDelete()}
      onPlan={() => void sel.onPlan()}
      onCreateProgram={() => void sel.onCreateProgram()}
    />
  ) : null;

  const calendar: ReactNode = (
    <View style={styles.calendar}>
      <MonthCalendar
        theme={theme}
        value={cal.day}
        onChange={cal.setDay}
        markers={cal.markers}
        done={cal.done}
        missed={cal.missedDays}
      />
    </View>
  );
  const dayList: ReactNode = (
    <PlannedDayList
      theme={theme}
      day={cal.day}
      plans={cal.dayPlans}
      sessions={cal.daySessions}
      missed={cal.missed}
      status={cal.status}
      ready={cal.ready}
      onOpenPlan={o.onOpenPlan}
      onStartPlan={o.onStartPlan}
      startingPlanId={o.startingPlanId}
      onOpenSession={o.onOpenSession}
      onPlanOn={cal.onPlanOn}
      onPlanAgain={cal.onPlanAgain}
    />
  );

  const selectionDetail: ReactNode = selecting ? (
    <View style={styles.detail}>
      <SelectionOrderList
        theme={theme}
        sessions={picked}
        title={t("mobile.workouts.pickedTitle")}
        note={
          picked.length === 1
            ? t("mobile.workouts.pickedNoteOne")
            : picked.length > 1
              ? t("mobile.workouts.pickedNoteMany")
              : undefined
        }
      />
    </View>
  ) : null;

  const names = model?.sessions.map(nameOf) ?? [];
  const dialog: ReactNode = model && o.unit ? (
    <SaveSessionAsModelDialog
      theme={theme}
      workouts={model.workouts}
      names={names}
      defaultName={names.join(" · ").slice(0, MODEL_NAME_MAX)}
      catalog={o.catalog}
      unit={o.unit}
      apiFetch={o.apiFetch}
      durableWrite={o.durableWrite}
      resolveName={o.resolveName}
      createProgram
      intro={
        <SelectionOrderList
          theme={theme}
          sessions={model.sessions}
          title={t("mobile.workouts.pickedDays", { count: model.sessions.length })}
          note={t("mobile.workouts.pickedWrongOrder")}
        />
      }
      onPlanFromModel={(m) => {
        end();
        o.onPlanFromModel(m);
      }}
      onClose={() => sel.setModel(null)}
    />
  ) : null;

  return {
    mode,
    /** The calendar's day, "YYYY-MM-DD". */
    day: cal.day,
    selecting,
    endSelection: end,
    /** Spread on WorkoutsView. */
    listProps: {
      /** #321: the sessions of the kind chosen, the picked ones always. */
      items: shown,
      /** #321: said when the filter leaves no session in the loaded pages. */
      emptyText: kind === "all" ? undefined : t("mobile.workouts.kindEmpty", { kind: labels[kind], defaultValue: "{{kind}}: no sessions in the loaded pages" }),
      selection: selecting ? sel.ids : null,
      onToggleSelect: sel.toggle,
      onStartSelect: sel.start,
    },
    header,
    bar,
    calendar,
    dayList,
    selectionDetail,
    dialog,
  };
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    headerBox: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
    },
    kind: { paddingHorizontal: s.s4, paddingBottom: s.s2 },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s2,
    },
    calendar: { padding: s.s3 },
    detail: { padding: s.s4 },
  });
}

export type WorkoutsDiary = ReturnType<typeof useWorkoutsDiary>;
